import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import * as echarts from 'echarts';

@Component({
  selector: 'app-mixed-stackbar-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './mixed-stackbar-chart.component.html',
  styleUrls: ['./mixed-stackbar-chart.component.scss'],
})
export class MixedStackbarChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() data: any;
  @ViewChild('chartContainer') chartContainerRef?: ElementRef<HTMLDivElement>;

  private chart?: echarts.ECharts;
  private renderTimer?: ReturnType<typeof setTimeout>;

  ngAfterViewInit(): void {
    this.scheduleRender();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.scheduleRender();
    }
  }

  ngOnDestroy(): void {
    this.clearRenderTimer();
    this.chart?.dispose();
    this.chart = undefined;
  }

  private scheduleRender(): void {
    this.clearRenderTimer();
    this.renderTimer = setTimeout(() => {
      this.renderTimer = undefined;
      this.renderChart();
    });
  }

  private renderChart(): void {
    const element = this.chartContainerRef?.nativeElement;
    if (!element || !this.data) {
      return;
    }

    this.chart?.dispose();
    this.chart = echarts.init(element);
    this.chart.setOption(this.buildChartOption());
    this.chart.resize();
  }

  private buildChartOption(): echarts.EChartsOption {
    const rows = this.resolveRows();
    const categoryKey = this.data?.categoryKey || 'category';
    const categories = this.data?.categories || rows.map((row) => row[categoryKey]);
    const barValues = this.data?.barValues || rows.map((row) => row[this.data?.barValueKey || 'sales']);
    const lineValues = this.data?.lineValues || rows.map((row) => row[this.data?.lineValueKey || 'margin']);
    const numericBarValues = this.toNumberList(barValues);
    const leftAxisMax = this.resolveLeftAxisMax(numericBarValues);
    const leftAxisInterval = this.resolveLeftAxisInterval(leftAxisMax);
    const rightAxisMax = this.resolveRightAxisMax(lineValues);
    const rightAxisMin = this.resolveRightAxisMin(barValues, leftAxisMax, rightAxisMax);
    const barColor = this.data?.barColor || '#c8191e';
    const lineColor = this.data?.lineColor || '#df6b70';
    const barSeriesData = this.buildBarSeriesData(barValues, lineValues, leftAxisMax, rightAxisMin, rightAxisMax, barColor);
    const titleFontSize = Number(this.data?.titleFontSize ?? 10);
    const axisFontSize = Number(this.data?.axisFontSize ?? 8);
    const valueFontSize = Number(this.data?.valueFontSize ?? 8);
    const axisLineWidth = Number(this.data?.axisLineWidth ?? 1);
    const splitLineWidth = Number(this.data?.splitLineWidth ?? 1);
    const lineWidth = Number(this.data?.lineWidth ?? 2);
    const symbolSize = Number(this.data?.symbolSize ?? 7);
    const symbolBorderWidth = Number(this.data?.symbolBorderWidth ?? 2);
    const barLabelSuffix = String(this.data?.barLabelSuffix || '').trim();
    const lineLabelSuffix = String(this.data?.lineLabelSuffix || '').trim();
    const lineLabelPosition = this.data?.lineLabelPosition || (this.data?.lineLabelsInsideTop === true ? 'bottom' : 'top');
    const hideZeroBarLabels = this.data?.hideZeroBarLabels === true;
    const hideZeroLineLabels = this.data?.hideZeroLineLabels === true;
    const hideZeroCategoryLabels = this.data?.hideZeroCategoryLabels === true;
    const hideBars = this.data?.hideBars === true;
    const hideZeroBars = this.data?.hideZeroBars === true;
    const renderedBarSeriesData = hideZeroBars
      ? this.applyZeroBarVisibility(barSeriesData)
      : barSeriesData;

    return {
      animation: false,
      backgroundColor: '#ffffff',
      grid: {
        left: this.data?.grid?.left ?? 36,
        right: this.data?.grid?.right ?? 38,
        top: this.data?.grid?.top ?? 32,
        bottom: this.data?.grid?.bottom ?? 30,
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#707070', width: axisLineWidth } },
        axisLabel: {
          show: this.data?.showCategoryLabels !== false,
          color: '#303030',
          fontSize: axisFontSize,
          margin: 8,
          formatter: (value: any, index: number) =>
            this.shouldHideZeroCategoryLabel(index, barValues, hideZeroCategoryLabels)
              ? ''
              : String(value ?? ''),
        },
      },
      yAxis: [
        {
          type: 'value',
          min: this.data?.leftAxis?.min ?? 0,
          max: leftAxisMax,
          interval: this.data?.leftAxis?.interval ?? leftAxisInterval,
          axisLine: { show: true, lineStyle: { color: '#707070', width: axisLineWidth } },
          axisTick: { show: false },
          axisLabel: {
            show: this.data?.leftAxis?.showLabels !== false,
            color: '#303030',
            fontSize: axisFontSize,
            margin: this.data?.leftAxis?.labelMargin ?? 8,
            formatter: (value: any) => this.formatAxisValue(value, this.data?.leftAxis?.suffix),
          },
          splitLine: {
            show: this.data?.leftAxis?.showSplitLine !== false,
            lineStyle: { color: '#dddddd', width: splitLineWidth },
          },
        },
        {
          type: 'value',
          min: rightAxisMin,
          max: rightAxisMax,
          interval: this.data?.rightAxis?.interval ?? 10,
          axisLine: { show: true, lineStyle: { color: '#707070', width: axisLineWidth } },
          axisTick: { show: false },
          axisLabel: {
            show: this.data?.rightAxis?.showLabels !== false,
            color: '#303030',
            fontSize: axisFontSize,
            formatter: (value: any) => {
              const numericValue = Number(value);
              if (this.data?.lineStartsAboveBars === true && numericValue < 0) {
                return '';
              }
              return this.formatAxisValue(
                this.normalizeShiftedAxisLabel(numericValue),
                this.data?.rightAxis?.suffix,
              );
            },
          },
          splitLine: { show: false },
        },
      ],
      graphic: this.buildTitleGraphics(titleFontSize),
      series: [
        {
          name: this.data?.barName || 'Sales',
          type: 'bar',
          data: renderedBarSeriesData,
          barWidth: this.data?.barWidth ?? 20,
          barMinHeight: this.data?.barMinHeight ?? 3,
          z: 2,
          itemStyle: {
            color: hideBars ? 'transparent' : barColor,
            opacity: hideBars ? 0 : 1,
          },
          label: {
            show: !hideBars,
            position: 'top',
            color: barColor,
            fontSize: valueFontSize,
            formatter: ({ value }: any) =>
              this.shouldHideZeroLabel(value, hideZeroBarLabels)
                ? ''
                : this.formatCompactValue(value, barLabelSuffix),
          },
        },
        {
          name: this.data?.lineName || 'Margin',
          type: 'line',
          yAxisIndex: 1,
          data: lineValues,
          z: 5,
          zlevel: 1,
          symbol: 'circle',
          symbolSize,
          lineStyle: {
            color: lineColor,
            width: lineWidth,
          },
          itemStyle: {
            color: '#ffffff',
            borderColor: lineColor,
            borderWidth: symbolBorderWidth,
          },
          label: {
            show: true,
            position: lineLabelPosition,
            color: lineColor,
            fontSize: valueFontSize,
            distance: Number(this.data?.lineLabelDistance ?? 8),
            formatter: ({ value }: any) =>
              this.shouldHideZeroLabel(value, hideZeroLineLabels)
                ? ''
                : this.formatCompactValue(value, lineLabelSuffix),
          },
        },
      ],
    };
  }

  private resolveRows(): any[] {
    const directRows = this.data?.rows || this.data?.data;
    return Array.isArray(directRows) ? directRows : [];
  }

  private buildTitleGraphics(titleFontSize: number): any[] {
    const commonStyle = {
      fill: '#2f2f2f',
      fontSize: titleFontSize,
      fontWeight: 400,
      textAlign: 'center',
      textVerticalAlign: 'middle',
    };

    if (this.data?.titlePlacement === 'side') {
      return [
        {
          type: 'text',
          left: this.data?.leftTitleLeft ?? 8,
          top: this.data?.sideTitleTop ?? 'middle',
          rotation: -Math.PI / 2,
          style: {
            ...commonStyle,
            text: this.data?.leftTitle || 'Sales ($M)',
          },
        },
        {
          type: 'text',
          right: this.data?.rightTitleRight ?? 8,
          top: this.data?.sideTitleTop ?? 'middle',
          rotation: Math.PI / 2,
          style: {
            ...commonStyle,
            text: this.data?.rightTitle || 'Margin (%)',
          },
        },
      ];
    }

    return [
      {
        type: 'text',
        left: this.data?.titleLeft ?? 36,
        top: this.data?.titleTop ?? 8,
        style: {
          ...commonStyle,
          textAlign: 'left',
          text: this.data?.leftTitle || 'Sales ($M)',
        },
      },
      {
        type: 'text',
        right: this.data?.titleRight ?? 38,
        top: this.data?.titleTop ?? 8,
        style: {
          ...commonStyle,
          textAlign: 'right',
          text: this.data?.rightTitle || 'Margin (%)',
        },
      },
    ];
  }

  private clearRenderTimer(): void {
    if (!this.renderTimer) {
      return;
    }

    clearTimeout(this.renderTimer);
    this.renderTimer = undefined;
  }

  // rightAxis.max "auto": keep rightAxis.autoMinMax (default 50) as the top unless a line value is
  // higher, then round up to the next interval with room for its label, so no point is cut off.
  private resolveRightAxisMax(lineValues: any[]): number {
    const configuredMax = this.data?.rightAxis?.max;
    if (String(configuredMax ?? '').toLowerCase() !== 'auto') {
      return Number(configuredMax ?? 50);
    }

    const floor = Number(this.data?.rightAxis?.autoMinMax ?? 50);
    const interval = Number(this.data?.rightAxis?.interval ?? 10);
    const step = Number.isFinite(interval) && interval > 0 ? interval : 10;
    const maxLine = Math.max(...this.toNumberList(lineValues), Number.NEGATIVE_INFINITY);
    if (!Number.isFinite(maxLine) || maxLine <= floor) {
      return floor;
    }

    return Math.ceil((maxLine + step / 2) / step) * step;
  }

  private resolveRightAxisMin(barValues: any[], leftAxisMax: number, rightAxisMax: number): number {
    if (this.data?.lineStartsAboveBars !== true) {
      return Number(this.data?.rightAxis?.min ?? 0);
    }

    const maxBar = Math.max(
      ...barValues.map((value) => Number(value)).filter((value) => Number.isFinite(value)),
      0,
    );
    const lineAboveBarPadding = Number(
      this.data?.lineAboveBarPadding ?? this.data?.lineAboveBarsPadding ?? 0.12,
    );
    const normalizedBarTop = leftAxisMax > 0
      ? Math.max(0.05, Math.min((maxBar / leftAxisMax) + lineAboveBarPadding, 0.85))
      : 0.5;

    return -((normalizedBarTop / Math.max(1 - normalizedBarTop, 0.15)) * rightAxisMax);
  }

  private buildBarSeriesData(
    barValues: any[],
    lineValues: any[],
    leftAxisMax: number,
    rightAxisMin: number,
    rightAxisMax: number,
    barColor: string,
  ): any[] {
    if (this.data?.avoidLabelOverlap === false) {
      return barValues;
    }

    const collisionThreshold = Number(this.data?.labelCollisionThreshold ?? 0.1);
    const minInsideBarRatio = Number(this.data?.minInsideLabelBarRatio ?? 0.16);

    return barValues.map((value, index) => {
      const barValue = Number(value);
      const lineValue = Number(lineValues[index]);
      if (!Number.isFinite(barValue) || !Number.isFinite(lineValue) || leftAxisMax <= 0 || rightAxisMax <= rightAxisMin) {
        return value;
      }

      const barTopRatio = barValue / leftAxisMax;
      const lineRatio = (lineValue - rightAxisMin) / (rightAxisMax - rightAxisMin);
      const isCollision = Math.abs(barTopRatio - lineRatio) <= collisionThreshold;
      const canFitInsideBar = barTopRatio >= minInsideBarRatio;
      if (!isCollision || !canFitInsideBar) {
        return value;
      }

      return {
        value,
        label: {
          position: 'insideTop',
          distance: Number(this.data?.insideBarLabelDistance ?? 3),
          color: this.data?.insideBarLabelColor || '#ffffff',
          backgroundColor: this.data?.insideBarLabelBackgroundColor || barColor,
          padding: this.data?.insideBarLabelPadding ?? [1, 2],
        },
      };
    });
  }

  private resolveLeftAxisMax(barValues: number[]): number {
    const configuredMax = this.data?.leftAxis?.max;
    const shouldAutoScale = this.data?.autoScaleLeftAxis === true || String(configuredMax || '').toLowerCase() === 'auto';
    const numericConfiguredMax = Number(configuredMax);

    if (!shouldAutoScale && Number.isFinite(numericConfiguredMax) && numericConfiguredMax > 0) {
      return numericConfiguredMax;
    }

    const maxBar = Math.max(...barValues.filter((value) => Number.isFinite(value)), 0);
    if (maxBar <= 0) {
      return Number.isFinite(numericConfiguredMax) && numericConfiguredMax > 0 ? numericConfiguredMax : 1;
    }

    return this.getNiceAxisMax(maxBar * 1.18);
  }

  private resolveLeftAxisInterval(axisMax: number): number | undefined {
    if (this.data?.leftAxis?.interval !== undefined && this.data?.leftAxis?.interval !== 'auto') {
      const configuredInterval = Number(this.data.leftAxis.interval);
      return Number.isFinite(configuredInterval) && configuredInterval > 0 ? configuredInterval : undefined;
    }

    return Number((axisMax / 3).toPrecision(2));
  }

  private getNiceAxisMax(value: number): number {
    if (!Number.isFinite(value) || value <= 0) {
      return 1;
    }

    const exponent = Math.floor(Math.log10(value));
    const magnitude = Math.pow(10, exponent);
    const normalized = value / magnitude;
    const niceNormalized = normalized <= 1
      ? 1
      : normalized <= 2
        ? 2
        : normalized <= 5
          ? 5
          : 10;

    return Number((niceNormalized * magnitude).toPrecision(12));
  }

  private toNumberList(values: any[]): number[] {
    return values
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value));
  }

  private formatAxisValue(value: any, suffix?: any): string {
    const formatted = this.formatCompactValue(value);
    return suffix ? `${formatted}${suffix}` : formatted;
  }

  private normalizeShiftedAxisLabel(value: number): number {
    if (!Number.isFinite(value) || this.data?.lineStartsAboveBars !== true) {
      return value;
    }

    const interval = Number(this.data?.rightAxis?.interval ?? 10);
    const tolerance = Number.isFinite(interval) && interval > 0
      ? Math.min(interval * 0.05, 0.5)
      : 0.5;
    const roundedValue = Math.round(value);

    return Math.abs(value - roundedValue) <= tolerance ? roundedValue : value;
  }

  private shouldHideZeroLabel(value: any, enabled: boolean): boolean {
    if (!enabled) {
      return false;
    }

    const numericValue = this.extractNumericValue(value);
    return Number.isFinite(numericValue) && Math.abs(numericValue) < 1e-9;
  }

  private shouldHideZeroCategoryLabel(index: number, barValues: any[], enabled: boolean): boolean {
    if (!enabled) {
      return false;
    }

    const numericValue = this.extractNumericValue(barValues?.[index]);
    return Number.isFinite(numericValue) && Math.abs(numericValue) < 1e-9;
  }

  private extractNumericValue(value: any): number {
    if (typeof value === 'number') {
      return value;
    }

    if (value && typeof value === 'object' && 'value' in value) {
      return Number((value as any).value);
    }

    return Number(value);
  }

  private applyZeroBarVisibility(values: any[]): any[] {
    return values.map((value) => {
      if (!this.shouldHideZeroLabel(value, true)) {
        return value;
      }

      if (value && typeof value === 'object') {
        return {
          ...value,
          itemStyle: {
            ...((value as any).itemStyle || {}),
            color: 'transparent',
            opacity: 0,
          },
        };
      }

      return {
        value,
        itemStyle: {
          color: 'transparent',
          opacity: 0,
        },
      };
    });
  }

  private formatCompactValue(value: any, suffix = ''): string {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return '-';
    }

    const absolute = Math.abs(parsed);
    let formatted: string;
    if (absolute >= 1_000_000) {
      formatted = `${this.trimNumber(parsed / 1_000_000)}M`;
    } else if (absolute >= 1_000) {
      formatted = `${this.trimNumber(parsed / 1_000)}K`;
    } else {
      formatted = this.trimNumber(parsed);
    }

    return suffix ? `${formatted}${suffix}` : formatted;
  }

  private trimNumber(value: number): string {
    const absolute = Math.abs(value);
    const decimals = absolute > 0 && absolute < 0.1
      ? 2
      : absolute > 0 && absolute < 1
        ? 1
        : 1;

    return Number(value.toFixed(decimals)).toString();
  }
}
